import { useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import I18n from '@/modules/i18n';
import * as Routes from '@/modules/routes';
import type { AIGeneratedQuestion, ModificationComparison } from './types';
import useAIQuestionBuilderStore from './useAIQuestionBuilderStore';

const POLL_INTERVAL = 2000;

const useQuestionGeneration = () => {
  const {
    answerCount,
    assessmentID,
    creationMode,
    generationStatus,
    questionCount,
    questions,
    selectedSectionIDs,
    setError,
    setGeneratedQuestions,
    setGenerationStatus,
    setJobID,
    setLlmCompletionID,
    setPanelView,
    setRateLimited,
  } = useAIQuestionBuilderStore();

  const pollTimerRef = useRef<number | null>(null);
  const cancelledRef = useRef(false);

  const isGenerating = generationStatus === 'generating';

  const questionsToModifyCount = questions
    ?.filter(q => answerCount !== null && q.number_of_answers !== answerCount).length ?? 0;

  const countsValid = answerCount !== null && answerCount >= 2 && answerCount <= 6
    && (creationMode === 'modify' || (questionCount !== null && questionCount >= 1 && questionCount <= 20));

  const canSubmit = countsValid && !isGenerating && (
    selectedSectionIDs.size > 0
    || (creationMode === 'modify' && questionsToModifyCount > 0)
  );

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const pollJobStatus = useCallback((jobID: string) => {
    cancelledRef.current = false;

    pollTimerRef.current = window.setInterval(async () => {
      if (cancelledRef.current) {
        stopPolling();
        return;
      }

      try {
        const { data } = await axios.get(
          Routes.plato_api_job_status_path(jobID),
        );
        const status = data.data;

        if (status.status === 'rate_limited') {
          stopPolling();
          setRateLimited(true);
          setGenerationStatus('idle');
          setPanelView('closed');
        } else if (status.status === 'failed') {
          stopPolling();
          setGenerationStatus('failed');
          setError(I18n.t('ai_qb_error_message'));
        } else if (status.status === 'completed') {
          stopPolling();
          const rawQuestions = status.metadata?.questions || [];

          // Transform modify mode response to match create mode structure
          const transformedQuestions: AIGeneratedQuestion[] = creationMode === 'modify'
            ? (rawQuestions as ModificationComparison[]).map(comparison => ({
              ...comparison.modified,
              id: comparison.question_id,
              original: { ...comparison.original },
              type: 'multiple_choice',
            }))
            : rawQuestions;

          setGeneratedQuestions(transformedQuestions);
          setLlmCompletionID(status.metadata?.llm_completion_id ?? null);
          setGenerationStatus('completed');
          setPanelView('review');
        }
      } catch {
        stopPolling();
        setGenerationStatus('failed');
        setError(I18n.t('ai_qb_error_message'));
      }
    }, POLL_INTERVAL);
  }, [
    creationMode, stopPolling, setGeneratedQuestions, setGenerationStatus,
    setError, setLlmCompletionID, setRateLimited, setPanelView,
  ]);

  const handleSubmit = useCallback(async () => {
    setGenerationStatus('generating');
    setError(null);

    try {
      const response = creationMode === 'modify'
        ? await axios.patch(
          Routes.plato_api_ai_question_builder_path(assessmentID),
          {
            answer_count: answerCount,
            assessment_id: assessmentID,
          },
        )
        : await axios.post(
          Routes.plato_api_ai_question_builder_index_path(),
          {
            answer_count: answerCount,
            assessment_id: assessmentID,
            question_count: questionCount,
            section_ids: Array.from(selectedSectionIDs),
          },
        );

      if (response.data.status === 'rate_limited') {
        setRateLimited(true);
        setGenerationStatus('idle');
        setPanelView('closed');
        return;
      }

      const jobID = response.data.job_id;
      setJobID(jobID);
      pollJobStatus(jobID);
    } catch {
      setGenerationStatus('failed');
      setError(I18n.t('ai_qb_error_message'));
    }
  }, [
    answerCount, assessmentID, creationMode, pollJobStatus, questionCount, selectedSectionIDs,
    setError, setGenerationStatus, setJobID, setPanelView, setRateLimited,
  ]);

  const handleCancel = () => {
    cancelledRef.current = true;
    stopPolling();
    setGenerationStatus('idle');
  };

  return { canSubmit, handleCancel, handleSubmit, isGenerating };
};

export default useQuestionGeneration;
